import {
  useId,
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { Send } from "lucide-react";
import { Button } from "../atoms/Button/Button.js";
import { ContextAttachmentChips } from "./ContextAttachmentChips.js";
import { PastedContextChips } from "./PastedContext.js";
import {
  ComposerSuggestionMenus,
  mentionOptionDomId,
  type ComposerMentionOption,
} from "./PlayerAIComposerSuggestions.js";
import { ComposerTextarea } from "./PlayerAIComposerTextarea.js";
import {
  ComposerStopControl,
  type PlayerAIRunControl,
} from "./ComposerRunControls.js";
import type { ComposerPastedContext } from "./PlayerAIComposer.js";

/** `playerAIMention`/`nextPhaseCommand`'s availability shape (PlayerAIRailProps). */
interface ComposerSuggestionAvailability {
  readonly available: boolean;
  readonly disabled?: boolean | undefined;
}

export interface ComposerInputBoxProps {
  runControl?: PlayerAIRunControl | undefined;
  groupConversation?: boolean | undefined;
  pastedContext: ComposerPastedContext;
  composerRef: MutableRefObject<HTMLTextAreaElement | null>;
  draft: string;
  setDraft: (value: string, caret: number, inputType?: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  composerDisabled: boolean;
  composerReadOnly: boolean;
  composerPlaceholder?: string | undefined;
  composerContextAttachments?:
    | readonly { id: string; name: string }[]
    | undefined;
  onRemoveContextAttachment?: ((id: string) => void) | undefined;
  composerLeadingSlot?: ReactNode | undefined;
  /** The mention picker is offered at all (Player AI and/or people). */
  mentionsAvailable: boolean;
  mentionMenuOpen: boolean;
  mentionOptions: readonly ComposerMentionOption[];
  mentionStatus?: "loading" | "error" | "ready" | undefined;
  onRetryMentions?: (() => void) | undefined;
  activeMentionIndex: number;
  onSelectMention: (option: ComposerMentionOption) => void;
  nextPhaseCommand?: ComposerSuggestionAvailability | undefined;
  slashMenuOpen: boolean;
  onSelectNextPhaseCommand: () => void;
}

/**
 * The composer's input box — the pasted-context/attachment chips, the two
 * hand-built suggestion listboxes (PlayerAIComposerSuggestions.tsx), the
 * leading slot, the raw `<textarea>` (its own auto-grow effect, combobox
 * role and aria wiring) and Send (PB-5422 chat-parts-private). Split into a
 * sibling of PlayerAIComposer.tsx to keep that file under the 200-line soft
 * cap (packages/ui/CLAUDE.md invariant 6). Moved as-is: no class, DOM or
 * behaviour change — the `useId` calls below now run inside this component,
 * so the generated id strings may differ from before (no spec or baseline
 * reads them).
 */
export function ComposerInputBox({
  runControl,
  groupConversation = false,
  pastedContext,
  composerRef,
  draft,
  setDraft,
  onKeyDown,
  composerDisabled,
  composerReadOnly,
  composerPlaceholder,
  composerContextAttachments,
  onRemoveContextAttachment,
  composerLeadingSlot,
  mentionsAvailable,
  mentionMenuOpen,
  mentionOptions,
  mentionStatus,
  onRetryMentions,
  activeMentionIndex,
  onSelectMention,
  nextPhaseCommand,
  slashMenuOpen,
  onSelectNextPhaseCommand,
}: ComposerInputBoxProps) {
  const mentionListId = useId();
  const activeMention = mentionOptions[activeMentionIndex];
  const mentionOptionId =
    mentionMenuOpen && activeMention
      ? mentionOptionDomId(mentionListId, activeMention)
      : undefined;
  const slashListId = useId();
  const slashOptionId = `${slashListId}-next-phase`;

  const inputBoxRef = useRef<HTMLDivElement | null>(null);

  // Auto-resize textarea up to its max-height. The input box reads the same
  // height as its floor (PB-5381), so a short surface shrinks the chips and
  // the region above instead of the box itself.
  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    inputBoxRef.current?.style.setProperty(
      "--composer-draft-height",
      `${el.scrollHeight}px`,
    );
  }, [composerRef, draft]);

  return (
    // The box never shrinks below its input row: the draft box (capped as
    // below, and never shorter than the 28px Send) plus the box's own
    // padding and border. Without this floor a short surface squeezed the
    // box, and the input row spilled out over the hint row and the
    // popover's edge.
    <div
      ref={inputBoxRef}
      className="relative flex min-h-[calc(max(1.75rem,min(var(--composer-draft-height,0px),7rem,calc(20dvh-0.5rem)))+0.75rem+2px)] min-w-0 flex-col bg-pb-card border border-pb-border rounded-lg px-2 py-1.5 focus-within:border-pb-iris/50 focus-within:shadow-glow-iris transition-[border-color,box-shadow]"
    >
      {composerContextAttachments?.length || pastedContext.items.length > 0 ? (
        // The chips stay in the input box (d8), in their own area that
        // shrinks with the surface; each group keeps its own 112px cap on
        // tall windows.
        <div data-composer-chips className="min-h-0 overflow-y-auto">
          {composerContextAttachments?.length ? (
            <div className="pb-3 pt-1">
              <ContextAttachmentChips
                items={composerContextAttachments}
                onRemove={onRemoveContextAttachment}
              />
            </div>
          ) : null}
          {pastedContext.items.length > 0 ? (
            <div className="pb-3 pt-1">
              <PastedContextChips
                items={pastedContext.items}
                onEdit={pastedContext.onEdit}
                onRemove={pastedContext.onRemove}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        data-composer-input-row
        className="flex min-w-0 shrink-0 items-center gap-2"
      >
        <ComposerSuggestionMenus
          mentionMenuOpen={mentionMenuOpen}
          mentionOptions={mentionOptions}
          mentionStatus={mentionStatus}
          onRetryMentions={onRetryMentions}
          activeMentionIndex={activeMentionIndex}
          mentionListId={mentionListId}
          onSelectMention={onSelectMention}
          slashMenuOpen={slashMenuOpen}
          nextPhaseCommandAvailable={nextPhaseCommand?.available}
          slashListId={slashListId}
          slashOptionId={slashOptionId}
          onSelectNextPhaseCommand={onSelectNextPhaseCommand}
        />
        {composerLeadingSlot ? (
          // One 28px cell. It does not clip, so a focus ring or a count
          // badge can overhang it.
          <div
            data-composer-leading
            className="flex h-7 w-7 shrink-0 items-center justify-center"
          >
            {composerLeadingSlot}
          </div>
        ) : null}
        <ComposerTextarea
          composerRef={composerRef}
          draft={draft}
          setDraft={setDraft}
          onKeyDown={onKeyDown}
          onPaste={pastedContext.onPaste}
          composerPlaceholder={composerPlaceholder}
          composerDisabled={composerDisabled}
          composerReadOnly={composerReadOnly}
          mentionsAvailable={mentionsAvailable}
          nextPhaseCommand={nextPhaseCommand}
          mentionMenuOpen={mentionMenuOpen}
          slashMenuOpen={slashMenuOpen}
          mentionListId={mentionListId}
          mentionOptionId={mentionOptionId}
          slashListId={slashListId}
          slashOptionId={slashOptionId}
        />
        {runControl ? (
          <ComposerStopControl
            control={runControl}
            alongsideSend={groupConversation}
          />
        ) : null}
        {/* Group participants can keep sending while the current run executes. */}
        {groupConversation || !runControl?.canStop ? (
          <Button
            type="submit"
            variant="primary"
            iconOnly
            size="sm"
            aria-label="Send"
            disabled={
              composerDisabled ||
              // Sending is the ONE thing a read-only composer refuses. The
              // reason is rendered beside the input by the host.
              composerReadOnly ||
              pastedContext.overLimit ||
              (!draft.trim() && pastedContext.items.length === 0)
            }
            className="shrink-0 self-center"
          >
            <Send className="w-3.5 h-3.5" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
